import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Loader2, Search, RefreshCw, ExternalLink, FileText, ArrowUp, ArrowDown, Copy, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const STATUS_TONES = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  pending_approval: 'bg-amber-50 text-amber-700 border-amber-200',
  cco_approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  sent_to_client: 'bg-blue-50 text-blue-700 border-blue-200',
  signed: 'bg-purple-50 text-purple-700 border-purple-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const SortHeader = ({ label, field, sort, sortDir, onSort }) => {
  const active = sort === field;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`inline-flex items-center gap-1 hover:text-[#0F2040] transition-colors ${active ? 'text-[#0F2040] font-semibold' : 'text-slate-500'}`}
      data-testid={`all-sows-sort-${field}`}
    >
      {label}
      {active && (sortDir === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)}
    </button>
  );
};

export default function AllSOWsTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [driftOnly, setDriftOnly] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [batchSyncing, setBatchSyncing] = useState(false);
  const [confirmBatchSync, setConfirmBatchSync] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/sows`, {
        params: {
          page, limit: PAGE_SIZE, search,
          sort, sort_dir: sortDir,
          drift: driftOnly ? 'true' : undefined,
        },
        withCredentials: true,
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load SOWs');
    } finally {
      setLoading(false);
    }
  }, [page, search, sort, sortDir, driftOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (field) => {
    if (field === sort) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };

  const handleSyncRow = async (sowId) => {
    setSyncingId(sowId);
    try {
      await axios.post(`${API_URL}/api/sow/${sowId}/sync-conditions-dates`, {}, { withCredentials: true });
      toast.success('SOW dates synced to current Project Conditions');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncingId(null);
    }
  };

  const handleBatchSync = async () => {
    setBatchSyncing(true);
    setConfirmBatchSync(false);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/sows/sync-drifted`,
        {},
        { withCredentials: true },
      );
      const r = res.data || {};
      const parts = [];
      if (r.synced) parts.push(`${r.synced} synced`);
      if (r.skipped) parts.push(`${r.skipped} skipped`);
      if (r.failed) parts.push(`${r.failed} failed`);
      const summary = parts.join(' · ') || 'no SOWs to sync';
      const cappedNote = r.capped ? ` (capped at ${r.max_sync} of ${r.total_drifted} drifted — re-run to sync the rest)` : '';
      if (r.failed > 0) {
        toast.error(`Batch sync: ${summary}${cappedNote}`);
      } else if (r.synced > 0) {
        toast.success(`Batch sync complete: ${summary}${cappedNote}`);
      } else {
        toast.info(`Batch sync: ${summary}${cappedNote}`);
      }
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Batch sync failed');
    } finally {
      setBatchSyncing(false);
    }
  };

  const totalPages = useMemo(
    () => (data?.total ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1),
    [data?.total],
  );

  return (
    <div className="space-y-4" data-testid="all-sows-tab">
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-[#0F2040] text-sm flex-wrap">
            <FileText className="w-4 h-4 text-[#4ABFCF]" />
            All Statements of Work
            {data && (
              <>
                <span className="ml-auto text-[11px] text-slate-500 font-normal" data-testid="all-sows-count">
                  {data.total} SOW{data.total === 1 ? '' : 's'} stored
                </span>
                {data.drift_count > 0 && (
                  <button
                    type="button"
                    onClick={() => { setDriftOnly(d => !d); setPage(1); }}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${
                      driftOnly
                        ? 'bg-amber-200 text-amber-900 border-amber-400'
                        : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
                    }`}
                    title={driftOnly ? 'Showing only drifted SOWs — click to clear filter' : 'Click to filter to drifted SOWs only'}
                    data-testid="all-sows-drift-badge"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    {data.drift_count} out of sync
                    {driftOnly && <span className="ml-0.5">×</span>}
                  </button>
                )}
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search by SOW ID, customer, estimate ID, status…"
                className="pl-8 text-xs h-8"
                data-testid="all-sows-search-input"
              />
            </div>
            <Button type="submit" size="sm" variant="outline" className="h-8 text-[11px]" data-testid="all-sows-search-btn">
              Search
            </Button>
            {search && (
              <Button
                type="button" size="sm" variant="ghost"
                onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
                className="h-8 text-[11px]"
                data-testid="all-sows-clear-search-btn"
              >
                Clear
              </Button>
            )}
            <Button type="button" size="sm" variant="outline" onClick={fetchData}
              disabled={loading} className="h-8 text-[11px] ml-auto" data-testid="all-sows-refresh-btn">
              {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Refresh
            </Button>
            {data?.drift_count > 0 && (
              <Button
                type="button" size="sm"
                onClick={() => setConfirmBatchSync(true)}
                disabled={loading || batchSyncing}
                className="h-8 text-[11px] bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="all-sows-batch-sync-btn"
              >
                {batchSyncing
                  ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  : <RefreshCw className="w-3 h-3 mr-1" />}
                Sync All Drifted ({data.drift_count})
              </Button>
            )}
          </form>

          {loading && !data ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading SOWs…
            </div>
          ) : !data?.sows?.length ? (
            <div className="text-sm text-slate-500 py-10 text-center" data-testid="all-sows-empty">
              {search ? 'No SOWs match your search.' : 'No SOWs stored yet.'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left">
                      <th className="px-3 py-2"><SortHeader label="SOW ID" field="sow_id" sort={sort} sortDir={sortDir} onSort={handleSort} /></th>
                      <th className="px-3 py-2"><SortHeader label="Customer" field="customer_legal_name" sort={sort} sortDir={sortDir} onSort={handleSort} /></th>
                      <th className="px-3 py-2 text-slate-500">Estimate</th>
                      <th className="px-3 py-2"><SortHeader label="Status" field="status" sort={sort} sortDir={sortDir} onSort={handleSort} /></th>
                      <th className="px-3 py-2"><SortHeader label="Created" field="created_at" sort={sort} sortDir={sortDir} onSort={handleSort} /></th>
                      <th className="px-3 py-2"><SortHeader label="Updated" field="updated_at" sort={sort} sortDir={sortDir} onSort={handleSort} /></th>
                      <th className="px-3 py-2 text-slate-500">Created By</th>
                      <th className="px-3 py-2 text-slate-500 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.sows.map(s => {
                      const tone = STATUS_TONES[s.status] || STATUS_TONES.draft;
                      return (
                        <tr key={s.sow_id} className="hover:bg-slate-50" data-testid={`all-sows-row-${s.sow_id}`}>
                          <td className="px-3 py-2 font-mono text-slate-700">
                            <button
                              type="button"
                              onClick={() => copyToClipboard(s.sow_id, 'SOW ID')}
                              className="inline-flex items-center gap-1 hover:text-[#0F2040]"
                              title="Click to copy"
                              data-testid={`all-sows-copy-id-${s.sow_id}`}
                            >
                              {s.sow_id}
                              <Copy className="w-2.5 h-2.5 text-slate-400" />
                            </button>
                            {s.sow_number && (
                              <div className="text-slate-400 text-[10px]">{s.sow_number}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-900 max-w-[200px] truncate">
                            {s.customer_legal_name || <span className="text-slate-400 italic">—</span>}
                          </td>
                          <td className="px-3 py-2 max-w-[200px]">
                            {s.estimate_id ? (
                              <div className="space-y-0.5">
                                <div className="text-slate-700 truncate">
                                  {s.estimate_name || <span className="text-slate-400 italic">(unnamed)</span>}
                                  {s.estimate_version ? <span className="text-slate-400 ml-1">v{s.estimate_version}</span> : null}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(s.estimate_id, 'Estimate ID')}
                                  className="font-mono text-[10px] text-slate-500 hover:text-[#0F2040] inline-flex items-center gap-1"
                                  title="Click to copy estimate ID"
                                >
                                  {s.estimate_id.slice(0, 12)}…
                                  <Copy className="w-2.5 h-2.5" />
                                </button>
                              </div>
                            ) : <span className="text-slate-400 italic">—</span>}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${tone}`}>
                                {(s.status || 'draft').replace(/_/g, ' ')}
                              </span>
                              {s.has_drift && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-300"
                                  title={`Out of sync with Project Conditions (${s.drift_fields.join(', ')}). Conditions: start ${s.conditions_start_date || '—'}, go-live ${s.conditions_go_live_date || '—'}.`}
                                  data-testid={`all-sows-drift-${s.sow_id}`}
                                >
                                  <AlertTriangle className="w-2.5 h-2.5" />
                                  drift
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{formatDateTime(s.created_at)}</td>
                          <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{formatDateTime(s.updated_at)}</td>
                          <td className="px-3 py-2 text-slate-600 max-w-[140px] truncate">{s.created_by_name || <span className="text-slate-400 italic">—</span>}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {s.has_drift && (
                              <Button
                                type="button" size="sm" variant="ghost"
                                onClick={() => handleSyncRow(s.sow_id)}
                                disabled={syncingId === s.sow_id}
                                className="h-6 px-2 text-[10px] text-amber-800 hover:bg-amber-50"
                                title="Sync this SOW's dates to current Project Conditions"
                                data-testid={`all-sows-sync-${s.sow_id}`}
                              >
                                {syncingId === s.sow_id
                                  ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  : <RefreshCw className="w-3 h-3 mr-1" />}
                                Sync
                              </Button>
                            )}
                            <Button
                              type="button" size="sm" variant="ghost"
                              onClick={() => navigate(`/sow/${s.sow_id}`)}
                              className="h-6 px-2 text-[10px]"
                              data-testid={`all-sows-open-sow-${s.sow_id}`}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Open SOW
                            </Button>
                            {s.estimate_id && (
                              <Button
                                type="button" size="sm" variant="ghost"
                                onClick={() => window.open(`/estimator?load=${s.estimate_id}`, '_blank', 'noopener')}
                                className="h-6 px-2 text-[10px]"
                                data-testid={`all-sows-open-estimate-${s.sow_id}`}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                Estimate
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
                  <div data-testid="all-sows-pagination-info">
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.total)} of {data.total}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1 || loading}
                      className="h-7 text-[10px]"
                      data-testid="all-sows-prev-page"
                    >
                      Prev
                    </Button>
                    <span className="px-2">Page {page} / {totalPages}</span>
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages || loading}
                      className="h-7 text-[10px]"
                      data-testid="all-sows-next-page"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmBatchSync} onOpenChange={setConfirmBatchSync}>
        <DialogContent data-testid="all-sows-batch-sync-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Sync All Drifted SOWs?
            </DialogTitle>
            <DialogDescription>
              This will pull <strong>{data?.drift_count || 0}</strong> drifted SOW{(data?.drift_count || 0) === 1 ? '' : 's'}&apos; Start Date and Go-Live Date back into alignment with their parent estimate&apos;s current Project Conditions, and re-set the inheritance flag.
              <br /><br />
              SOWs already <em>sent to client</em> or <em>signed</em> will be skipped (they&rsquo;re immutable). The batch is capped at <strong>25</strong> per run — if more SOWs drift, run this again.
              <br /><br />
              You can manually re-edit any SOW&rsquo;s dates afterwards.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmBatchSync(false)} disabled={batchSyncing}
              data-testid="all-sows-batch-sync-cancel">
              Cancel
            </Button>
            <Button type="button" onClick={handleBatchSync} disabled={batchSyncing}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="all-sows-batch-sync-confirm">
              {batchSyncing
                ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                : <RefreshCw className="w-3 h-3 mr-1" />}
              Sync {data?.drift_count || 0} SOW{(data?.drift_count || 0) === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
